import apiClient from './apiClient';
import { handleApiError, formatApiResponse } from './api';

/**
 * Servicio para gestión de formulaciones
 * Basado en la API documentada en formulaciones_module.md
 */
const formulacionesService = {
  // ==================== GESTIÓN BÁSICA DE FORMULACIONES ====================

  /**
   * Obtener lista de formulaciones
   * @param {Object} params - Parámetros de filtrado
   * @param {number} params.skip - Offset para paginación
   * @param {number} params.limit - Límite de resultados
   * @param {boolean} params.is_active - Filtrar por activas
   * @param {string} params.company_name - Filtrar por compañía
   * @param {string} params.formula_type - Filtrar por tipo (betel, naturalandlab, etc.)
   * @param {string} params.search - Búsqueda por nombre
   */
  getFormulaciones: async (params = {}) => {
    try {
      const response = await apiClient.get('/formulas', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener formulación por ID
   */
  getFormulacion: async (id) => {
    try {
      const response = await apiClient.get(`/formulas/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Crear nueva formulación
   * @param {Object} data - Datos de la formulación
   * @param {string} data.company_name - Nombre de la compañía
   * @param {string} data.formula_type - Tipo de fórmula (betel, naturalandlab, etc.)
   * @param {string} data.product_name - Nombre del producto
   * @param {string} data.container_type - Tipo de contenedor (capsules, powder, tea, etc.)
   * @param {number} data.quantity_per_container - Cantidad por contenedor
   * @param {string} data.quantity_unit - Unidad (UNIT, GRAM, ML, etc.)
   * @param {string} data.capsule_type - Tipo de cápsula si aplica
   * @param {number} data.mixing_time_minutes - Tiempo de mezcla en minutos
   * @param {number} data.best_use_date_years - Años de mejor uso
   * @param {boolean} data.includes_silica - Incluye sílica
   * @param {number} data.silica_cost - Costo de sílica
   * @param {Array} data.ingredients - Array de ingredientes
   * @param {string} data.notes - Notas adicionales
   */
  createFormulacion: async (data) => {
    try {
      console.log('Creando formulación con datos:', JSON.stringify(data, null, 2));
      const response = await apiClient.post('/formulas', data);
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error en createFormulacion:', error);
      console.error('Response data:', error.response?.data);
      console.error('Response status:', error.response?.status);
      return handleApiError(error);
    }
  },

  /**
   * Actualizar formulación
   */
  updateFormulacion: async (id, data) => {
    try {
      console.log('Actualizando formulación con datos:', data);
      const response = await apiClient.put(`/formulas/${id}`, data);
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error en updateFormulacion:', error);
      return handleApiError(error);
    }
  },

  /**
   * Eliminar formulación
   */
  deleteFormulacion: async (id) => {
    try {
      const response = await apiClient.delete(`/formulas/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ==================== GESTIÓN DE INGREDIENTES ====================

  /**
   * Obtener ingredientes de una formulación
   */
  getIngredientes: async (formulaId) => {
    try {
      const response = await apiClient.get(`/formulas/${formulaId}/ingredients`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Agregar ingrediente a formulación
   */
  addIngrediente: async (formulaId, ingredientData) => {
    try {
      const response = await apiClient.post(`/formulas/${formulaId}/ingredients`, ingredientData);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Actualizar ingrediente de formulación
   */
  updateIngrediente: async (formulaId, ingredientId, data) => {
    try {
      const response = await apiClient.put(`/formulas/${formulaId}/ingredients/${ingredientId}`, data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Eliminar ingrediente de formulación
   */
  deleteIngrediente: async (formulaId, ingredientId) => {
    try {
      const response = await apiClient.delete(`/formulas/${formulaId}/ingredients/${ingredientId}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ==================== CÁLCULOS Y COSTEO ====================

  /**
   * Calcular costos de formulación (POST)
   */
  calcularCostos: async (formulaId, calculationData) => {
    try {
      const response = await apiClient.post(`/formulas/${formulaId}/calculate`, calculationData);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener cálculo de costos (GET)
   */
  getCostCalculation: async (formulaId, params = {}) => {
    try {
      const response = await apiClient.get(`/formulas/${formulaId}/cost-calculation`, { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Escalar formulación
   */
  escalarFormulacion: async (formulaId, targetQuantity, scaleFactor = null) => {
    try {
      const params = { target_quantity: targetQuantity };
      if (scaleFactor) params.scale_factor = scaleFactor;

      const response = await apiClient.post(`/formulas/${formulaId}/scale`, null, { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener información nutricional
   */
  getNutritionalInfo: async (formulaId, servingSize = 1) => {
    try {
      const response = await apiClient.get(`/formulas/${formulaId}/nutritional-info`, {
        params: { serving_size: servingSize }
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Comparar costos entre formulaciones
   */
  compareCosts: async (formulaIds, productionQuantity = 1000) => {
    try {
      const params = {
        formula_ids: Array.isArray(formulaIds) ? formulaIds : [formulaIds],
        production_quantity: productionQuantity
      };
      const response = await apiClient.get('/formulas/cost-comparison', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ==================== VERSIONADO Y CONTROL ====================

  /**
   * Obtener historial de versiones
   */
  getVersions: async (formulaId) => {
    try {
      const response = await apiClient.get(`/formulas/${formulaId}/versions`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Crear nueva versión
   */
  createVersion: async (formulaId, versionNotes = '') => {
    try {
      const response = await apiClient.post(`/formulas/${formulaId}/versions`, null, {
        params: { version_notes: versionNotes }
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener log de cambios
   */
  getChanges: async (formulaId, limit = 50) => {
    try {
      const response = await apiClient.get(`/formulas/${formulaId}/changes`, {
        params: { limit }
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Aprobar formulación
   */
  aprobarFormulacion: async (formulaId, approvalNotes = '') => {
    try {
      const response = await apiClient.post(`/formulas/${formulaId}/approve`, null, {
        params: { approval_notes: approvalNotes }
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ==================== INTEGRACIÓN CON PRODUCCIÓN ====================

  /**
   * Generar BOM (Bill of Materials)
   */
  getBOM: async (formulaId, productionQuantity = 1000) => {
    try {
      const response = await apiClient.get(`/formulas/${formulaId}/bom`, {
        params: { production_quantity: productionQuantity }
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Crear orden de producción
   */
  createProductionOrder: async (formulaId, orderData) => {
    try {
      const response = await apiClient.post(`/formulas/${formulaId}/production-order`, null, {
        params: orderData
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener requerimientos de inventario
   */
  getInventoryRequirements: async (formulaIds, productionQuantities) => {
    try {
      const params = {
        formula_ids: Array.isArray(formulaIds) ? formulaIds : [formulaIds],
        production_quantities: Array.isArray(productionQuantities) ? productionQuantities : [productionQuantities]
      };
      const response = await apiClient.get('/formulas/inventory-requirements', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener registros de lotes producidos
   */
  getBatchRecords: async (formulaId, params = {}) => {
    try {
      const response = await apiClient.get(`/formulas/${formulaId}/batch-records`, { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ==================== REPORTES Y ANÁLISIS ====================

  /**
   * Reporte de análisis de costos
   */
  getCostAnalysisReport: async (params = {}) => {
    try {
      const response = await apiClient.get('/formulas/reports/cost-analysis', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Reporte de uso de ingredientes
   */
  getIngredientUsageReport: async (params = {}) => {
    try {
      const response = await apiClient.get('/formulas/reports/ingredient-usage', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Reporte de rentabilidad
   */
  getProfitabilityReport: async (params = {}) => {
    try {
      const response = await apiClient.get('/formulas/reports/profitability', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Historial de lotes por formulación
   */
  getBatchHistoryReport: async (formulaId, params = {}) => {
    try {
      const response = await apiClient.get(`/formulas/${formulaId}/reports/batch-history`, { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ==================== UTILIDADES Y BÚSQUEDA ====================

  /**
   * Obtener estadísticas de formulaciones
   */
  getStats: async () => {
    try {
      const response = await apiClient.get('/formulas/stats');
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Validar formulación
   */
  validateFormulacion: async (formulaData) => {
    try {
      const response = await apiClient.post('/formulas/validate', formulaData);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Duplicar formulación
   */
  duplicarFormulacion: async (formulaId, duplicateData) => {
    try {
      const response = await apiClient.post(`/formulas/${formulaId}/duplicate`, duplicateData);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener sugerencias de búsqueda
   */
  getSearchSuggestions: async (query, limit = 10) => {
    try {
      const response = await apiClient.get('/formulas/search/suggestions', {
        params: { q: query, limit }
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener tipos de formulación disponibles
   */
  getFormulaTypes: async () => {
    try {
      const response = await apiClient.get('/formulas/types');
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener tipos de contenedor disponibles
   */
  getContainerTypes: async () => {
    try {
      const response = await apiClient.get('/formulas/container-types');
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener tipos de cápsula disponibles
   */
  getCapsuleTypes: async () => {
    try {
      const response = await apiClient.get('/formulas/capsule-types');
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Generar PDF de formulación
   */
  generarPDF: async (id) => {
    try {
      const response = await apiClient.get(`/formulas/${id}/pdf`, {
        responseType: 'blob'
      });
      return {
        success: true,
        data: response.data,
        headers: response.headers
      };
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener formulación por ID de producto
   */
  obtenerFormulacionPorProducto: async (productId) => {
    try {
      const response = await apiClient.get(`/products/${productId}/formulation`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Calcular costo de formulación (alias de calcularCostos)
   */
  calcularCostoFormulacion: async (formulaId, calculationData) => {
    return formulacionesService.calcularCostos(formulaId, calculationData);
  }
};

export default formulacionesService;
